import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useLocationStore } from '@/store/location';
import type { Database } from '@/lib/database.types';
import type { LocationSettings } from '@/hooks/useLocations';

type PaymentType = Database['public']['Tables']['payment_types']['Row'];
type LocationPaymentType = Database['public']['Tables']['location_payment_types']['Row'];

export interface PaymentTypeWithLocations extends PaymentType {
  location_payment_types?: LocationPaymentType[];
}

export function usePaymentTypes() {
  const queryClient = useQueryClient();
  const { organization } = useAuth();
  const { currentLocation } = useLocationStore();
  const organizationId = organization?.id;
  const locationId = currentLocation?.id;

  // Fetch all payment types for organization
  const {
    data: paymentTypes,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['payment-types', organizationId],
    queryFn: async () => {
      if (!organizationId) return [];

      const { data, error } = await supabase
        .from('payment_types')
        .select('*, location_payment_types(*)')
        .eq('organization_id', organizationId)
        .order('sort_order')
        .order('name');

      if (error) throw error;
      return data as PaymentTypeWithLocations[];
    },
    enabled: !!organizationId,
  });

  // Fetch payment types available at current location
  const {
    data: locationPaymentTypes,
    isLoading: locationPaymentTypesLoading,
  } = useQuery({
    queryKey: ['location-payment-types', locationId],
    queryFn: async () => {
      if (!locationId || !organizationId) return [];

      // Get all payment types that are:
      // 1. Enabled for this location via location_payment_types
      // 2. OR have no location restrictions (available everywhere)
      const { data: locationEnabled, error: locError } = await supabase
        .from('location_payment_types')
        .select('payment_type_id')
        .eq('location_id', locationId)
        .eq('is_enabled', true);

      if (locError) throw locError;

      const enabledIds = new Set(locationEnabled?.map(l => l.payment_type_id) || []);

      // Get all payment types for org
      const { data: allTypes, error } = await supabase
        .from('payment_types')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('sort_order')
        .order('name');

      if (error) throw error;

      // Filter to only enabled types for this location
      // If no location_payment_types records exist, include all active types
      if (locationEnabled && locationEnabled.length > 0) {
        return (allTypes as PaymentType[]).filter(pt => enabledIds.has(pt.id));
      }
      
      // No restrictions set - return all active payment types
      return allTypes as PaymentType[];
    },
    enabled: !!locationId && !!organizationId,
  });

  // Create payment type
  const createPaymentType = useMutation({
    mutationFn: async (input: {
      name: string;
      code: string;
      is_cash_equivalent?: boolean;
      sort_order?: number;
    }) => {
      if (!organizationId) throw new Error('No organization');

      const { data, error } = await supabase
        .from('payment_types')
        .insert({
          organization_id: organizationId,
          name: input.name,
          code: input.code.toLowerCase().replace(/[^a-z0-9_]/g, '_'),
          is_cash_equivalent: input.is_cash_equivalent ?? false,
          sort_order: input.sort_order ?? 0,
          is_active: true,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-types', organizationId] });
    },
  });

  // Update payment type
  const updatePaymentType = useMutation({
    mutationFn: async ({ id, updates }: {
      id: string;
      updates: Partial<{
        name: string;
        code: string;
        is_cash_equivalent: boolean;
        is_active: boolean;
        is_primary: boolean;
        sort_order: number;
      }>;
    }) => {
      const { data, error } = await supabase
        .from('payment_types')
        .update({
          ...updates,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-types', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['location-payment-types'] });
    },
  });

  // Set a payment type as primary (and unset others)
  const setPrimaryPaymentType = useMutation({
    mutationFn: async (id: string) => {
      if (!organizationId) throw new Error('No organization');

      // First, unset all others as primary
      await supabase
        .from('payment_types')
        .update({ is_primary: false, updated_at: new Date().toISOString() })
        .eq('organization_id', organizationId);

      // Then set the selected one as primary
      const { data, error } = await supabase
        .from('payment_types')
        .update({ is_primary: true, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-types', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['location-payment-types'] });
    },
  });

  // Update sort orders for multiple payment types
  const updateSortOrders = useMutation({
    mutationFn: async (orderedIds: string[]) => {
      // Update each payment type with its new sort order
      const updates = orderedIds.map((id, index) => 
        supabase
          .from('payment_types')
          .update({ sort_order: index, updated_at: new Date().toISOString() })
          .eq('id', id)
      );

      await Promise.all(updates);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-types', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['location-payment-types'] });
    },
  });

  // Delete payment type
  const deletePaymentType = useMutation({
    mutationFn: async (id: string) => {
      // Soft delete
      const { error } = await supabase
        .from('payment_types')
        .update({ is_active: false })
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-types', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['location-payment-types'] });
    },
  });

  // Enable/disable payment type for a location
  const setLocationPaymentType = useMutation({
    mutationFn: async ({ 
      paymentTypeId, 
      targetLocationId, 
      isEnabled 
    }: { 
      paymentTypeId: string; 
      targetLocationId: string;
      isEnabled: boolean;
    }) => {
      // Check if record exists
      const { data: existing } = await supabase
        .from('location_payment_types')
        .select('id')
        .eq('payment_type_id', paymentTypeId)
        .eq('location_id', targetLocationId)
        .single();

      if (existing) {
        const { error } = await supabase
          .from('location_payment_types')
          .update({ is_enabled: isEnabled })
          .eq('payment_type_id', paymentTypeId)
          .eq('location_id', targetLocationId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('location_payment_types')
          .insert({
            payment_type_id: paymentTypeId,
            location_id: targetLocationId,
            is_enabled: isEnabled,
          });

        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['payment-types', organizationId] });
      queryClient.invalidateQueries({ queryKey: ['location-payment-types'] });
    },
  });

  // Get the hide default setting from current location
  const hideDefaultPaymentTypes = (currentLocation?.settings as LocationSettings | null)?.hide_default_payment_types ?? false;

  return {
    // All payment types for org (admin view)
    paymentTypes: paymentTypes ?? [],
    isLoading,
    error,
    refetch,

    // Payment types available at current location (POS view)
    locationPaymentTypes: locationPaymentTypes ?? [],
    locationPaymentTypesLoading,

    // Location setting for hiding defaults
    hideDefaultPaymentTypes,

    // Mutations
    createPaymentType,
    updatePaymentType,
    deletePaymentType,
    setLocationPaymentType,
    setPrimaryPaymentType,
    updateSortOrders,
  };
}

